import { type AllOrNothing } from '@src/types/utils';
import { Checkbox } from '@components/input/Checkbox.tsx';
import { Icon } from '@components/Icon.tsx';
import { IconType } from '@src/IconType.ts';
import {
  type RequiredSortingPaginatedTableProps,
  type SelectionProps,
} from '@components/table/types';
import { Skeleton } from '@components/Skeleton.tsx';
import { classNames } from '@utils/classNames.ts';
import { difference, isSuperset, union } from '@utils/setOperations.ts';
import { useCallback } from 'react';

export default function TableHead<
  T,
  AdditionalProps extends Record<string, unknown>,
>({
  columns,
  totalCount,
  sort,
  setSort,
  data,
  getId,
  ...rest
}: Pick<
  RequiredSortingPaginatedTableProps<T, AdditionalProps>,
  'columns' | 'totalCount' | 'sort' | 'setSort' | 'data' | 'getId'
> &
  AllOrNothing<SelectionProps>) {
  const sortButtonFunction = useCallback(
    (fieldName: string) => () => {
      if (sort?.fieldName === fieldName) {
        if (sort.direction === 'DESC') {
          setSort(null);
        } else {
          setSort({ fieldName, direction: 'DESC' });
        }
      } else {
        setSort({ fieldName, direction: 'ASC' });
      }
    },
    [sort, setSort],
  );

  const getSortArrow = (fieldName: string) => {
    if (sort?.fieldName === fieldName) {
      if (sort.direction === 'DESC') {
        return <Icon type={IconType.ArrowDown} className="h-3 w-3 ml-2" />;
      } else {
        return <Icon type={IconType.ArrowUp} className="h-3 w-3 ml-2" />;
      }
    } else {
      return <Icon type={IconType.ArrowUpDown} className="h-3 w-3 ml-2" />;
    }
  };

  return (
    <thead className="sticky top-0 z-[var(--z-index-sticky-header)] bg-white hidden sm:table-header-group">
      <tr className="border-b border-blue-100">
        {rest.selectedIds && (
          <th scope="col" className="items-center justify-center pl-4 sm:w-12">
            <Checkbox
              data-testid="select-all-rows-checkbox"
              checked={rest.selectedIds.size === totalCount}
              onChange={() => {
                rest.setSelectedIds((prevState) => {
                  const currentItemsAsSet = new Set(data.map(getId));
                  const currentPageSelected = isSuperset(
                    prevState,
                    currentItemsAsSet,
                  );
                  return currentPageSelected
                    ? difference(prevState, currentItemsAsSet)
                    : union(prevState, currentItemsAsSet);
                });
              }}
              indeterminate={
                rest.selectedIds.size > 0 &&
                rest.selectedIds.size !== totalCount
              }
            />
          </th>
        )}
        {columns.map((column, index) => {
          if (!column) {
            return <TableHeadPlaceholder key={index} />;
          }

          const fieldNameToSort = column.customSortFieldName
            ? column.customSortFieldName
            : column.identifier;

          return (
            <th
              key={column.identifier}
              scope="col"
              className={classNames(
                'px-3 py-3.5 text-left text-sm font-medium text-slate-500',
                column.headClassName,
              )}
            >
              <button
                className="flex items-center uppercase"
                onClick={
                  column.sortable
                    ? sortButtonFunction(fieldNameToSort)
                    : undefined
                }
                data-testid={`sort-${column.identifier}-button`}
              >
                {column.headContent}
                {column.sortable && getSortArrow(fieldNameToSort)}
              </button>
            </th>
          );
        })}
      </tr>
    </thead>
  );
}

const TableHeadPlaceholder = () => {
  return (
    <th className="px-3 py-3.5 text-sm">
      <Skeleton />
    </th>
  );
};
